/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import { coerceNumberProperty } from '@angular/cdk/coercion';
import { Directive, forwardRef, Input, OnChanges } from '@angular/core';
import { VIRTUAL_SCROLL_STRATEGY, VirtualScrollStrategy, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { sortedIndex } from 'lodash';

/** Virtual scrolling strategy for lists with items of known fixed size. */
export class KnownSizesVirtualScrollStrategy implements VirtualScrollStrategy {
    private _scrolledIndexChange = new Subject<number>();

    /** @docs-private Implemented as part of VirtualScrollStrategy. */
    scrolledIndexChange: Observable<number> = this._scrolledIndexChange.pipe(distinctUntilChanged());

    /** The attached viewport. */
    private _viewport: CdkVirtualScrollViewport | null = null;

    /** Sorted array of the offsets of the items in the virtually scrolling list. */
    private _itemOffsets: number[];

    /** The minimum amount of buffer rendered beyond the viewport (in pixels). */
    private _minBufferPx: number;

    /** The number of buffer items to render beyond the edge of the viewport (in pixels). */
    private _maxBufferPx: number;

    /**
     * @param itemSize The size of the items in the virtually scrolling list.
     * @param minBufferPx The minimum amount of buffer (in pixels) before needing to render more
     * @param maxBufferPx The amount of buffer (in pixels) to render when rendering more.
     */
    constructor(itemOffsets: number[], minBufferPx: number, maxBufferPx: number) {
        this._itemOffsets = itemOffsets;
        this._minBufferPx = minBufferPx;
        this._maxBufferPx = maxBufferPx;
    }

    /**
     * Attaches this scroll strategy to a viewport.
     *
     * @param viewport The viewport to attach this strategy to.
     */
    attach(viewport: CdkVirtualScrollViewport) {
        this._viewport = viewport;
        this._updateTotalContentSize();
        this._updateRenderedRange();
    }

    /** Detaches this scroll strategy from the currently attached viewport. */
    detach() {
        this._scrolledIndexChange.complete();
        this._viewport = null;
    }

    /**
     * Update the item size and buffer size.
     *
     * @param itemSize The size of the items in the virtually scrolling list.
     * @param minBufferPx The minimum amount of buffer (in pixels) before needing to render more
     * @param maxBufferPx The amount of buffer (in pixels) to render when rendering more.
     */
    updateItemAndBufferSize(itemSizes: number[], minBufferPx: number, maxBufferPx: number) {
        if (maxBufferPx < minBufferPx) {
            throw Error('CDK virtual scroll: maxBufferPx must be greater than or equal to minBufferPx');
        }
        this._itemOffsets = itemSizes;
        this._minBufferPx = minBufferPx;
        this._maxBufferPx = maxBufferPx;
        this._updateTotalContentSize();
        this._updateRenderedRange();
    }

    /** @docs-private Implemented as part of VirtualScrollStrategy. */
    onContentScrolled() {
        this._updateRenderedRange();
    }

    /** @docs-private Implemented as part of VirtualScrollStrategy. */
    onDataLengthChanged() {
        this._updateTotalContentSize();
        this._updateRenderedRange();
    }

    /** @docs-private Implemented as part of VirtualScrollStrategy. */
    onContentRendered() {
        /* no-op */
    }

    /** @docs-private Implemented as part of VirtualScrollStrategy. */
    onRenderedOffsetChanged() {
        /* no-op */
    }

    /**
     * Scroll to the offset for the given index.
     *
     * @param index The index of the element to scroll to.
     * @param behavior The ScrollBehavior to use when scrolling.
     */
    scrollToIndex(index: number, behavior: ScrollBehavior): void {
        if (this._viewport && this._itemOffsets && this._itemOffsets.length >= index && index >= 0) {
            this._viewport.scrollToOffset(this._itemOffsets[index], behavior);
        }
    }

    /** Update the viewport's total content size. */
    private _updateTotalContentSize() {
        if (!this._viewport || !this._itemOffsets || this._itemOffsets.length === 0) {
            return;
        }

        this._viewport.setTotalContentSize(this._itemOffsets[this._itemOffsets.length - 1]);
    }

    private getIndexFromPosition(pos: number) {
        return Math.max(0, sortedIndex(this._itemOffsets, pos) - 1);
    }

    /** Update the viewport's rendered range. */
    private _updateRenderedRange() {
        if (!this._viewport || !this._itemOffsets || this._itemOffsets.length === 0) {
            return;
        }

        const scrollOffset = this._viewport.measureScrollOffset();
        const firstVisibleIndex = this.getIndexFromPosition(scrollOffset);
        const renderedRange = this._viewport.getRenderedRange();
        const newRange = { start: renderedRange.start, end: renderedRange.end };
        const viewportSize = this._viewport.getViewportSize();
        const dataLength = this._viewport.getDataLength();

        if (newRange.start > this._itemOffsets.length) {
            newRange.start = 0;
            newRange.end = this._itemOffsets.length - 1;
        }

        const startBuffer = scrollOffset - this._itemOffsets[newRange.start];
        if (startBuffer < this._minBufferPx && newRange.start !== 0) {
            const expandStart = this.getIndexFromPosition(this._maxBufferPx - startBuffer);
            newRange.start = Math.max(0, newRange.start - expandStart);
            newRange.end = Math.min(
                dataLength,
                Math.ceil(this.getIndexFromPosition(scrollOffset + viewportSize + this._minBufferPx))
            );
        } else {
            const endBuffer = this._itemOffsets[newRange.end] - (scrollOffset + viewportSize);
            if (endBuffer < this._minBufferPx && newRange.end !== dataLength) {
                const expandEnd = this.getIndexFromPosition(this._maxBufferPx - endBuffer);
                if (expandEnd > 0) {
                    newRange.end = Math.min(dataLength, newRange.end + expandEnd);
                    newRange.start = Math.max(
                        0,
                        Math.floor(this.getIndexFromPosition(this._itemOffsets[firstVisibleIndex] - this._minBufferPx))
                    );
                }
            }
        }

        this._viewport.setRenderedRange(newRange);
        this._viewport.setRenderedContentOffset(this._itemOffsets[newRange.start]);
        this._scrolledIndexChange.next(Math.floor(firstVisibleIndex));
    }
}

/**
 * Provider factory for `FixedSizeVirtualScrollStrategy` that simply extracts the already created
 * `FixedSizeVirtualScrollStrategy` from the given directive.
 *
 * @param fixedSizeDir The instance of `CdkFixedSizeVirtualScroll` to extract the
 *     `FixedSizeVirtualScrollStrategy` from.
 */
export function _knownSizesVirtualScrollStrategyFactory(knownSizesDir: KnownSizesVirtualScroll) {
    return knownSizesDir._scrollStrategy;
}

/** A virtual scroll strategy that supports fixed-size items. */
@Directive({
    // eslint-disable-next-line @angular-eslint/directive-selector
    selector: 'cdk-virtual-scroll-viewport[itemOffsets]',
    providers: [
        {
            provide: VIRTUAL_SCROLL_STRATEGY,
            useFactory: _knownSizesVirtualScrollStrategyFactory,
            deps: [forwardRef(() => KnownSizesVirtualScroll)]
        }
    ],
    standalone: true
})
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export class KnownSizesVirtualScroll implements OnChanges {
    /** The size of the items in the list (in pixels). */
    @Input()
    get itemOffsets(): number[] {
        return this._itemOffsets;
    }
    set itemOffsets(value: number[]) {
        this._itemOffsets = value;
    }
    _itemOffsets = [0];

    /**
     * The minimum amount of buffer rendered beyond the viewport (in pixels).
     * If the amount of buffer dips below this number, more items will be rendered. Defaults to 100px.
     */
    @Input()
    get minBufferPx(): number {
        return this._minBufferPx;
    }
    set minBufferPx(value: number) {
        this._minBufferPx = coerceNumberProperty(value);
    }
    _minBufferPx = 200;

    /**
     * The number of pixels worth of buffer to render for when rendering new items. Defaults to 200px.
     */
    @Input()
    get maxBufferPx(): number {
        return this._maxBufferPx;
    }
    set maxBufferPx(value: number) {
        this._maxBufferPx = coerceNumberProperty(value);
    }
    _maxBufferPx = 300;

    /** The scroll strategy used by this directive. */
    _scrollStrategy = new KnownSizesVirtualScrollStrategy(this.itemOffsets, this.minBufferPx, this.maxBufferPx);

    ngOnChanges() {
        this._scrollStrategy.updateItemAndBufferSize(this.itemOffsets, this.minBufferPx, this.maxBufferPx);
    }
}
